import { useEffect, useRef, useState } from "react";
import { getClosedLoopReaders, getFlexyBoxMembershipTypes, type FlexyBoxMembershipType } from "../../api/packageDeal.ts";
import styles from "../../styles/formPage.module.scss";

/**
 * External Reference editor with an optional "Closed Loop Specific" builder.
 *
 * Closed-loop references follow the kiosk's matching format (LoginClosedLoop.ApplySubsidy,
 * PackageDeal.ClosedLoopMatchLength):
 *   - FlexyBox:   `cl|flexybox|<guid>§<guid>…`  (one GUID per membership type)
 *   - Others:     `cl|<reader>|<usergroup>|<usersubgroup>`  (MyCompanee: the numeric group ids)
 *
 * The prefix is meaningful on its own: `ccl` tells the kiosk that the closed loop covers the
 * reduction — the customer sees the reduced price, the closed loop is charged the full price
 * (ClosedLoopHandler.GetAmount). `cl` charges the reduced price. The kiosk matches both spellings
 * alike, so the prefix is kept exactly as stored and only changed through its own checkbox.
 */

const FLEXYBOX = "flexybox";
const MYCOMPANEE = "mycompanee";

interface ParsedClosedLoop {
  isClosedLoop: boolean;
  reader: string;
  groups: string[];
  userGroup: string;
  userSubGroup: string;
  /** True for the `ccl` spelling: the closed loop is charged the full price and covers the reduction. */
  closedLoopPays: boolean;
}

export function parseClosedLoopReference(raw: string): ParsedClosedLoop {
  const value = (raw || "").trim();
  const lower = value.toLowerCase();
  const isCl = lower === "cl" || lower.startsWith("cl|") || lower === "ccl" || lower.startsWith("ccl|");
  if (!isCl) {
    return { isClosedLoop: false, reader: "", groups: [], userGroup: "", userSubGroup: "", closedLoopPays: false };
  }
  const closedLoopPays = lower.startsWith("ccl");
  const parts = value.split("|");
  const reader = (parts[1] ?? "").trim().toLowerCase();
  if (reader === FLEXYBOX) {
    const groups = parts.slice(2).join("|").split("§").map((g) => g.trim()).filter(Boolean);
    return { isClosedLoop: true, reader, groups, userGroup: "", userSubGroup: "", closedLoopPays };
  }
  return {
    isClosedLoop: true,
    reader,
    groups: [],
    userGroup: (parts[2] ?? "").trim(),
    userSubGroup: (parts[3] ?? "").trim(),
    closedLoopPays,
  };
}

export function buildClosedLoopReference(reader: string, groups: string[], userGroup: string, userSubGroup: string, closedLoopPays: boolean): string {
  const r = reader.trim().toLowerCase();
  // A FlexyBox customer pays from their own wallet, so "the closed loop covers the reduction" has no meaning there.
  const prefix = closedLoopPays && r !== FLEXYBOX ? "ccl" : "cl";
  const parts = r === FLEXYBOX
    ? [prefix, r, groups.join("§")]
    : [prefix, r, userGroup.trim(), userSubGroup.trim()];
  while (parts.length > 1 && !parts[parts.length - 1]) parts.pop();
  return parts.join("|");
}

/** Split a free-text GUID input on whitespace, commas, semicolons, pipes, or §. */
function parseManualGuids(text: string): string[] {
  return text.split(/[\s,;|§]+/).map((g) => g.trim()).filter(Boolean);
}

export function ExternalReferenceField({ value, onChange }: {
  value: string;
  onChange: (v: string) => void;
}) {
  // Seeded once from the incoming value; afterwards this component is the only writer
  // while the builder is active. Re-mounting (tab switch) re-seeds from the current value.
  const [initial] = useState(() => parseClosedLoopReference(value));
  const [closedLoop, setClosedLoop] = useState(initial.isClosedLoop);
  const [reader, setReader] = useState(initial.reader);
  const [userGroup, setUserGroup] = useState(initial.userGroup);
  const [userSubGroup, setUserSubGroup] = useState(initial.userSubGroup);
  /** `ccl` spelling: the closed loop is charged the full price and covers the reduction. Kept as stored. */
  const [closedLoopPays, setClosedLoopPays] = useState(initial.closedLoopPays);
  /** GUIDs picked from the loaded membership-type list. */
  const [selected, setSelected] = useState<string[]>(initial.groups);
  /** GUIDs typed by hand (unknown to the list, or entered before/without it). */
  const [manualGuids, setManualGuids] = useState("");

  const [types, setTypes] = useState<FlexyBoxMembershipType[] | null>(null);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState<string | null>(null);

  /** Readers actually configured on this endpoint's kiosks (uppercase, for display). */
  const [readers, setReaders] = useState<string[] | null>(null);

  const isFlexybox = reader.trim().toLowerCase() === FLEXYBOX;

  /** True when the reader was typed by hand rather than picked from the list. */
  const [customReader, setCustomReader] = useState(false);

  // Load the endpoint's configured closed-loop readers the first time the builder opens.
  const readersLoadedRef = useRef(false);
  useEffect(() => {
    if (!closedLoop || readersLoadedRef.current) return;
    readersLoadedRef.current = true;
    getClosedLoopReaders()
      .then((list) => {
        setReaders(list);
        setReader((prev) => {
          const current = prev.trim().toLowerCase();
          // No reader picked yet (fresh reference): default to the endpoint's only/first one. A fresh
          // MyCompanee reference defaults to the closed loop covering the reduction, as it always did.
          if (!current) {
            const first = (list[0] ?? "").toLowerCase();
            if (!initial.isClosedLoop) setClosedLoopPays(first === MYCOMPANEE);
            return first;
          }
          // An existing reference naming a reader unknown to this endpoint stays editable as custom.
          if (!list.some((r) => r.toLowerCase() === current)) setCustomReader(true);
          return prev;
        });
      })
      .catch(() => setReaders([]));
  }, [closedLoop]);

  // Load membership types once, the first time the FlexyBox branch is shown.
  const typesLoadedRef = useRef(false);
  useEffect(() => {
    if (!closedLoop || !isFlexybox || typesLoadedRef.current) return;
    typesLoadedRef.current = true;
    setTypesLoading(true);
    getFlexyBoxMembershipTypes()
      .then((list) => {
        setTypes(list);
        // GUIDs seeded from an existing reference that aren't in the list move to
        // the manual input so nothing silently disappears from the built value.
        setSelected((prev) => {
          const known = new Set(list.map((t) => t.Id.toLowerCase()));
          const unknown = prev.filter((g) => !known.has(g.toLowerCase()));
          if (unknown.length) setManualGuids((m) => [m, ...unknown].filter(Boolean).join(" "));
          return prev.filter((g) => known.has(g.toLowerCase()));
        });
      })
      .catch((err) => setTypesError(err instanceof Error ? err.message : "Failed to load membership types"))
      .finally(() => setTypesLoading(false));
  }, [closedLoop, isFlexybox]);

  /** Rebuild the reference from (possibly overridden) form state and push it up. */
  const emit = (next: Partial<{ reader: string; selected: string[]; manual: string; userGroup: string; userSubGroup: string; closedLoopPays: boolean }>) => {
    const r = next.reader ?? reader;
    const sel = next.selected ?? selected;
    const man = next.manual ?? manualGuids;
    const seen = new Set<string>();
    const groups = [...sel, ...parseManualGuids(man)].filter((g) => {
      const k = g.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    onChange(buildClosedLoopReference(r, groups, next.userGroup ?? userGroup, next.userSubGroup ?? userSubGroup, next.closedLoopPays ?? closedLoopPays));
  };

  const handleToggle = (checked: boolean) => {
    setClosedLoop(checked);
    if (!checked) return; // keep the built value; the raw input takes over
    // Re-parse whatever is in the field right now so a hand-typed "cl|…" round-trips.
    const p = parseClosedLoopReference(value);
    if (p.isClosedLoop) {
      setReader(p.reader);
      setUserGroup(p.userGroup);
      setUserSubGroup(p.userSubGroup);
      setClosedLoopPays(p.closedLoopPays);
      if (types) {
        const known = new Set(types.map((t) => t.Id.toLowerCase()));
        setSelected(p.groups.filter((g) => known.has(g.toLowerCase())));
        setManualGuids(p.groups.filter((g) => !known.has(g.toLowerCase())).join(" "));
      } else {
        setSelected(p.groups);
        setManualGuids("");
      }
      onChange(buildClosedLoopReference(p.reader, p.groups, p.userGroup, p.userSubGroup, p.closedLoopPays));
    } else {
      emit({});
    }
  };

  const toggleGuid = (id: string, checked: boolean) => {
    const next = checked
      ? [...selected.filter((g) => g.toLowerCase() !== id.toLowerCase()), id.toLowerCase()]
      : selected.filter((g) => g.toLowerCase() !== id.toLowerCase());
    setSelected(next);
    emit({ selected: next });
  };

  return (
    <>
      <label className={styles.checkLabel}>
        <input type="checkbox" checked={closedLoop} onChange={(e) => handleToggle(e.target.checked)} />
        Closed Loop Specific
      </label>

      {!closedLoop ? (
        <div className={styles.fieldRowStacked}>
          <label>External Reference</label>
          <input
            className={styles.fieldInput}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      ) : (
        <>
          <div className={styles.fieldRowStacked}>
            <label>Closed loop</label>
            <select
              className={styles.fieldSelect}
              value={customReader ? "__custom__" : reader.trim().toLowerCase()}
              onChange={(e) => {
                if (e.target.value === "__custom__") {
                  setCustomReader(true);
                  return;
                }
                setCustomReader(false);
                setReader(e.target.value);
                emit({ reader: e.target.value });
              }}
            >
              {readers === null && <option value="">Loading…</option>}
              {readers !== null && !reader.trim() && !customReader && <option value="">Select closed loop</option>}
              {(readers ?? []).map((r) => <option key={r} value={r.toLowerCase()}>{r}</option>)}
              <option value="__custom__">Custom…</option>
            </select>
            {customReader && (
              <input
                className={styles.fieldInput}
                value={reader}
                placeholder="Closed-loop name"
                aria-label="Custom closed-loop name"
                onChange={(e) => { setReader(e.target.value); emit({ reader: e.target.value }); }}
              />
            )}
          </div>

          {isFlexybox ? (
            <>
              <div className={styles.fieldRowStacked}>
                <label>Membership types</label>
                {typesLoading && <span className={styles.hint}>Loading membership types…</span>}
                {typesError && <span className={`${styles.statusMsg} ${styles.statusError}`}>{typesError}</span>}
                {types && types.length === 0 && !typesLoading && (
                  <span className={styles.hint}>No membership types available.</span>
                )}
                {types && types.length > 0 && (
                  <div className={styles.checkList}>
                    {types.map((t) => (
                      <label key={t.Id} className={styles.checkLabel}>
                        <input
                          type="checkbox"
                          checked={selected.some((g) => g.toLowerCase() === t.Id.toLowerCase())}
                          onChange={(e) => toggleGuid(t.Id, e.target.checked)}
                        />
                        {t.Name?.trim() || t.Id}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.fieldRowStacked}>
                <label>Additional membership GUIDs</label>
                <input
                  className={styles.fieldInput}
                  value={manualGuids}
                  placeholder="GUIDs separated by space, comma or §"
                  onChange={(e) => { setManualGuids(e.target.value); emit({ manual: e.target.value }); }}
                />
              </div>
            </>
          ) : (
            <div className={styles.fieldRow} style={{ gap: "var(--space-3)" }}>
              <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
                <label>User group</label>
                <input
                  className={styles.fieldInput}
                  value={userGroup}
                  onChange={(e) => { setUserGroup(e.target.value); emit({ userGroup: e.target.value }); }}
                />
              </div>
              <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
                <label>User sub group</label>
                <input
                  className={styles.fieldInput}
                  value={userSubGroup}
                  onChange={(e) => { setUserSubGroup(e.target.value); emit({ userSubGroup: e.target.value }); }}
                />
              </div>
            </div>
          )}

          {!isFlexybox && (
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={closedLoopPays}
                onChange={(e) => { setClosedLoopPays(e.target.checked); emit({ closedLoopPays: e.target.checked }); }}
              />
              The closed loop covers the reduction (subsidies only): the customer sees the reduced price, the closed loop is charged the full price
            </label>
          )}

          <span className={styles.hint}>External reference: {value || buildClosedLoopReference(reader, [], "", "", closedLoopPays)}</span>
        </>
      )}
    </>
  );
}
